/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TeamMemberManager from '../user/TeamMemberManager';
import ProjectChat from './ProjectChat';
import InvoiceDistributionDialog from './InvoiceDistributionDialog';
import InvoicePaymentsDialog from './InvoicePaymentsDialog';
import { getUserDisplayName, getUserDisplayEmail, getUserStatusDisplay, isUserDeleted } from '../../utils/userUtils';
import { displayAccountNumber } from '../../utils/formatUtils';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography as MuiTypography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CalendarToday,
  LocationOn,
  People,
  Science,
  LocalFlorist,
  WaterDrop,
  Grass,
  AccessTime,
  Comment,
  AttachMoney,
  Extension,
  Refresh,
  Receipt,
  Thermostat,
  Water,
  Light,
  Co2,
  ArrowBack,
  Delete as DeleteIcon,
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import RequestSelector from '../user/requests/RequestSelector';
import DynamicRequestView from './requests/DynamicRequestView';
import DynamicRequestForm from './requests/DynamicRequestForm';
import { requestStatusColor, requestStatusLabel } from '../../utils/requestStatus';
import ProjectDocuments from './ProjectDocuments';
import { formatUsagePeriod } from '../../utils/usagePeriods';
import { formatClock } from '../../utils/timeFormat';



// Helper function to extract invoice number from invoice_id
// e.g., "202601270728_INV011" -> 11
const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = invoiceId.split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

const ProjectRequestsTab = ({ project, requests = [], refresh }) => {
  const [mode, setMode] = useState('list');
  // Which request type is open inside the New Request flow. Held here so the
  // header's back button can step back to the type list rather than dropping
  // the user all the way out to the requests table.
  const [newRequestType, setNewRequestType] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  // The dialog renders a row from the list, which goes stale the moment
  // someone posts in the thread, so re-read that request and refresh the list.
  const reloadViewing = async () => {
    if (!viewing?._id) return;
    try {
      const { data } = await axios.get(`/api/requests/${viewing._id}`);
      setViewing(data);
    } catch (err) {
      console.error('Error reloading request:', err);
    }
    if (refresh) refresh();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`/api/requests/${deleting._id}`);
      toast.success('Request deleted');
      setDeleting(null);
      if (refresh) refresh();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (mode === 'new') {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            <Extension sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
            New Request
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              if (newRequestType) {
                setNewRequestType(null);
              } else {
                setMode('list');
                if (refresh) refresh();
              }
            }}
          >
            {newRequestType ? 'Back to Request Types' : 'Back to Requests'}
          </Button>
        </Stack>
        <RequestSelector
          project={project}
          selectedKey={newRequestType}
          onSelectKey={setNewRequestType}
          onSubmitted={() => { setMode('list'); setNewRequestType(null); if (refresh) refresh(); }}
        />
      </Paper>
    );
  }

  if (editing) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Edit Request</Typography>
          <Button variant="outlined" onClick={() => setEditing(null)}>Cancel</Button>
        </Stack>
        <DynamicRequestForm
          request={editing}
          mode="edit"
          onCancel={() => setEditing(null)}
          onSubmitted={() => { setEditing(null); if (refresh) refresh(); }}
        />
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          <Extension sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
          Project Requests
        </Typography>
        <Button variant="contained" onClick={() => { setNewRequestType(null); setMode('new'); }}>
          New Request
        </Button>
      </Stack>

      {requests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            No requests submitted for this project yet.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Admin Update</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((r) => {
                const lastAdmin = (r.admin_responses || []).slice(-1)[0];
                return (
                  <TableRow key={r._id}>
                    <TableCell>{r.request_id}</TableCell>
                    <TableCell>{r.request_type}</TableCell>
                    <TableCell>{new Date(r.submitted_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={requestStatusLabel(r.status)}
                        color={requestStatusColor(r.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {lastAdmin ? (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(lastAdmin.responded_at).toLocaleDateString()}
                          </Typography>
                          {lastAdmin.comment && (
                            <Typography variant="body2" sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {lastAdmin.comment}
                            </Typography>
                          )}
                        </Box>
                      ) : <em>—</em>}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button size="small" variant="outlined" onClick={() => setViewing(r)}>
                          View
                        </Button>
                        {/* No revision cycle: while a request is open the team
                            edits the form and the change is logged. */}
                        {r.permissions?.can_edit && (
                          <Button size="small" onClick={() => setEditing(r)}>
                            Edit
                          </Button>
                        )}
                        {r.permissions?.can_delete && (
                          <Button size="small" color="error" onClick={() => setDeleting(r)}>
                            Delete
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
        <DialogTitle>Request Details</DialogTitle>
        <DialogContent dividers>
          {viewing && (
            <DynamicRequestView
              request={viewing}
              onChanged={reloadViewing}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewing(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => setDeleting(null)}>
        <DialogTitle>Delete request?</DialogTitle>
        <DialogContent>
          <Typography>This will permanently remove <strong>{deleting?.request_id}</strong>.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

const ProjectView = ({ user, project: propProject, embedded = false, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: urlProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const initialProject = propProject || location.state?.project;
  const [project, setProject] = useState(initialProject);
  const [selectedTab, setSelectedTab] = useState(() => searchParams.get('tab') || 'overview');
  const [chatSubTab, setChatSubTab] = useState(() => searchParams.get('chat') || 'team_chat');

  // ?tab= is also how a notification deep-links to the Requests or Invoices tab.
  // The initial state above only covers a fresh mount, so follow later changes
  // too — clicking a notification from the bell while already viewing a project
  // swaps the query string without remounting.
  const tabFromUrl = searchParams.get('tab');
  useEffect(() => {
    if (tabFromUrl) setSelectedTab(tabFromUrl);
  }, [tabFromUrl]);
  const [teamChatMuted, setTeamChatMuted] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [activityUnreadCount, setActivityUnreadCount] = useState(0);
  const [teamChatUnreadCount, setTeamChatUnreadCount] = useState(0);
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [newEndDate, setNewEndDate] = useState(null);
  const [error, setError] = useState('');
  const [requests, setRequests] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [distributionDialogOpen, setDistributionDialogOpen] = useState(false);
  const [distributionInvoice, setDistributionInvoice] = useState(null);
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [paymentsInvoice, setPaymentsInvoice] = useState(null);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeInvoice, setDisputeInvoice] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [isSubmittingDispute, setIsSubmittingDispute] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chamberDetails, setChamberDetails] = useState({});
  const [unitDetails, setUnitDetails] = useState({});
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('Staff');
  const [newMemberFirstName, setNewMemberFirstName] = useState('');
  const [newMemberLastName, setNewMemberLastName] = useState('');
  const [newMemberDepartment, setNewMemberDepartment] = useState('');
  const [newMemberCustomDepartment, setNewMemberCustomDepartment] = useState('');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);

  const emailCheckTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get('/api/departments');
        setDepartments(response.data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    fetchDepartments();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
  }, []);

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setNewMemberEmail(email);
    
    // Clear any existing timeout
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
      emailCheckTimeoutRef.current = null;
    }

    // Reset states when email changes but don't trigger loading state yet
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFoundUser(null);
      setIsNewUser(false);
      setIsCheckingEmail(false);
      return;
    }

    // Set a new timeout with debounce - only check after user stops typing
    emailCheckTimeoutRef.current = setTimeout(async () => {
      setIsCheckingEmail(true);
      try {
        const response = await axios.get(`/api/users/check-email/${encodeURIComponent(email)}`);
        if (response.data.exists) {
          setFoundUser({
            ...response.data.user,
            firstName: response.data.user.first_name,
            lastName: response.data.user.last_name,
          });
          setIsNewUser(false);
          // Pre-fill department if available
          if (response.data.user.department) {
            setNewMemberDepartment(response.data.user.department._id || response.data.user.department);
          }
          toast.success(`User ${email} found and details added`);
        } else {
          setIsNewUser(true);
          setFoundUser(null);
          toast.info('User not found. You can add them as a new team member.');
        }
      } catch (error) {
        console.error('Error checking email:', error);
        setIsNewUser(true);
        setFoundUser(null);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 800); // 800ms debounce - wait for user to stop typing
  };

  const handleAddMember = async () => {
    // Validate required fields
    if (!newMemberEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMemberEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (isNewUser) {
      if (!newMemberFirstName.trim()) {
        toast.error('Please enter the first name');
        return;
      }
      if (!newMemberLastName.trim()) {
        toast.error('Please enter the last name');
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const requestData = {
        email: foundUser ? foundUser.email : newMemberEmail,
        role: newMemberRole,
        isNewUser: isNewUser
      };

      // Add new user details if creating a new user
      if (isNewUser) {
        requestData.firstName = newMemberFirstName;
        requestData.lastName = newMemberLastName;
        requestData.department = newMemberDepartment || null;
        if (newMemberDepartment === 'not_listed') {
          requestData.customDepartment = newMemberCustomDepartment;
        }
      }

      const response = await axios.post(`/api/projects/${project._id}/team`, requestData);

      toast.success(isNewUser 
        ? 'New team member added successfully. They can sign up to access this project.' 
        : 'Team member added successfully');
      
      // Update the project state with the new data
      setProject(response.data);
      setAddMemberDialogOpen(false);
      resetAddMemberForm();
    } catch (error) {
      console.error('Error adding team member:', error);
      toast.error(error.response?.data?.error || 'Failed to add team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (email) => {
    try {
      const response = await axios.delete(`/api/projects/${project._id}/team/${encodeURIComponent(email)}`);
      toast.success('Team member removed successfully');
      // Update the project state with the new data
      setProject(response.data);
    } catch (error) {
      console.error('Error removing team member:', error);
      toast.error(error.response?.data?.error || 'Failed to remove team member');
    }
  };

  const resetAddMemberForm = () => {
    // Clear any pending email check
    if (emailCheckTimeoutRef.current) {
      clearTimeout(emailCheckTimeoutRef.current);
      emailCheckTimeoutRef.current = null;
    }
    setNewMemberEmail('');
    setNewMemberRole('Staff');
    setNewMemberFirstName('');
    setNewMemberLastName('');
    setNewMemberDepartment('');
    setNewMemberCustomDepartment('');
    setFoundUser(null);
    setIsNewUser(false);
    setIsCheckingEmail(false);
  };

  const handleRoleChange = async (email, newRole) => {
    try {
      const response = await axios.patch(`/api/projects/${project._id}/team/${encodeURIComponent(email)}/role`, {
        role: newRole
      });
      
      // Update the project state with the new data
      setProject(response.data);
      toast.success('Team member role updated successfully');
    } catch (error) {
      console.error('Error updating team member role:', error);
      toast.error(error.response?.data?.error || 'Failed to update team member role');
    }
  };

  const fetchProjectData = async () => {
    if (!project?._id) {
      // Arriving by URL (e.g. a notification deep link) mounts this with no
      // project yet — the effect below is still fetching it by its URL id. That
      // is a normal in-between state, not a missing id, so stay in loading
      // rather than flashing an error the tabs would then render.
      if (urlProjectId) {
        setLoading(true);
        return;
      }
      console.error('No project ID available');
      setError('Proposal ID is missing');
      setLoading(false);
      return;
    }
    
    let requestsRes, invoicesRes;
    
    try {
      setLoading(true);
      // Clear any error left over from a previous attempt, so a stale message
      // cannot outlive the load that fixed it.
      setError('');
      
      // First try to fetch the project details to ensure it exists
      const projectRes = await axios.get(`/api/projects/${project._id}`);
      if (!projectRes.data) {
        throw new Error('Project not found');
      }

      // Update project state with the fetched data
      setProject(projectRes.data);
      
      [requestsRes, invoicesRes] = await Promise.all([
        axios.get(`/api/requests/project/${project._id}`),
        axios.get(`/api/invoices/by-project/${project._id}`)
      ]);
      
      
      setRequests(requestsRes.data);
      // Ensure we're setting an array of invoices
      const invoicesData = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
      setInvoices(invoicesData);

      // Collect all chamber and unit IDs that need to be fetched
      const chamberIds = new Set();
      const unitIds = new Set();
      const fetchedProject = projectRes.data;
      
      if (fetchedProject.chamber_assignments) {
        fetchedProject.chamber_assignments.forEach(assignment => {
          // Get chamber ID (could be populated object or just ID string)
          const chamberId = typeof assignment.chamber === 'object' 
            ? assignment.chamber._id 
            : assignment.chamber;
          if (chamberId) chamberIds.add(chamberId);
          
          // Get unit IDs from each unit assignment
          assignment.units?.forEach(unitAssignment => {
            const unitId = typeof unitAssignment.unit === 'object' 
              ? unitAssignment.unit._id 
              : unitAssignment.unit;
            if (unitId) unitIds.add(unitId);
          });
        });
      }

      // Fetch chamber details
      const chamberMap = {};
      if (chamberIds.size > 0) {
        const chamberPromises = Array.from(chamberIds).map(id => 
          axios.get(`/api/chambers/${id}`).catch(error => {
            console.warn(`Failed to fetch chamber ${id}:`, error);
            return null;
          })
        );
        const chamberResponses = await Promise.all(chamberPromises);
        chamberResponses.forEach(response => {
          if (response?.data) {
            const chamber = response.data;
            chamberMap[chamber._id] = chamber;
          }
        });
      }

      // Fetch unit details
      const unitMap = {};
      if (unitIds.size > 0) {
        const unitPromises = Array.from(unitIds).map(id => 
          axios.get(`/api/units/${id}`).catch(error => {
            console.warn(`Failed to fetch unit ${id}:`, error);
            return null;
          })
        );
        const unitResponses = await Promise.all(unitPromises);
        unitResponses.forEach(response => {
          if (response?.data) {
            const unit = response.data;
            unitMap[unit._id] = unit;
          }
        });
      }
      
      setChamberDetails(chamberMap);
      setUnitDetails(unitMap);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching project data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // Only set error if we failed to fetch critical data (project, requests, or invoices)
      if (!requestsRes?.data || !invoicesRes?.data) {
        setError(error.response?.data?.message || 'Failed to fetch project data');
      }
      setLoading(false);
    }
  };

  // If no project is available from props/state but we have a URL param, fetch it
  useEffect(() => {
    if (!project && urlProjectId) {
      axios.get(`/api/projects/${urlProjectId}`)
        .then(res => {
          if (res.data) setProject(res.data);
        })
        .catch(err => {
          console.error('Error fetching project by ID:', err);
          setError('Failed to load project');
          setLoading(false);
        });
    }
  }, [urlProjectId, project]);

  useEffect(() => {
    fetchProjectData();
  }, [project?._id]);

  // Sync project state when propProject changes (e.g., parent component updates)
  useEffect(() => {
    if (propProject && propProject._id !== project?._id) {
      setProject(propProject);
    }
  }, [propProject]);

  // Determine if current user is PI or Co-PI on this project
  const userId = user?._id || user?.id;
  const currentUserEntry = project?.users?.find(u => {
    const entryUserId = u.user?._id || u.user;
    return entryUserId === userId || (entryUserId && entryUserId.toString() === userId?.toString());
  });
  const userProjectRole = currentUserEntry?.role?.role || currentUserEntry?.role;
  const isPIorCoPI = ['PI', 'Co-PI'].includes(userProjectRole);
  const isResourceOnly = project?.application_type === 'resource_only';

  // Set default chat sub-tab based on user role and project type
  useEffect(() => {
    if (isResourceOnly) {
      setChatSubTab('activity');
    } else if (isPIorCoPI) {
      setChatSubTab('activity');
    } else {
      setChatSubTab('team_chat');
    }
  }, [isPIorCoPI, isResourceOnly]);

  // If the active tab is one we hide for resource-only projects, bounce back
  // to overview so the user isn't stranded on a blank pane.
  useEffect(() => {
    if (isResourceOnly && selectedTab === 'requests') {
      setSelectedTab('overview');
    }
  }, [isResourceOnly, selectedTab]);

  // Fetch chat unread counts per channel
  useEffect(() => {
    if (!project?._id) return;
    let actCount = 0;
    let teamCount = 0;

    const fetchCounts = async () => {
      try {
        if (isPIorCoPI) {
          const actRes = await axios.get(`/api/projects/${project._id}/chat/unread-count?channel=activity`);
          actCount = actRes.data.unreadCount || 0;
          setActivityUnreadCount(actCount);
        }
        const teamRes = await axios.get(`/api/projects/${project._id}/chat/unread-count?channel=team_chat`);
        teamCount = teamRes.data.unreadCount || 0;
        setTeamChatUnreadCount(teamCount);
        setChatUnreadCount(actCount + teamCount);
      } catch { /* ignore */ }
    };
    fetchCounts();
  }, [project?._id, isPIorCoPI]);

  // Fetch team chat mute status from project notification preferences
  useEffect(() => {
    if (!project?._id) return;
    const fetchMuteStatus = async () => {
      try {
        const res = await axios.get(`/api/notifications/preferences/${project._id}`);
        const prefs = res.data?.preferences;
        setTeamChatMuted(prefs?.general_comment?.in_app === false);
      } catch { /* ignore */ }
    };
    fetchMuteStatus();
  }, [project?._id]);

  const toggleTeamChatMute = async () => {
    try {
      const res = await axios.get(`/api/notifications/preferences/${project._id}`);
      const prefs = res.data?.preferences || {};
      const currentInApp = prefs.general_comment?.in_app ?? true;
      const updated = {
        ...prefs,
        general_comment: { ...prefs.general_comment, in_app: !currentInApp }
      };
      await axios.put(`/api/notifications/preferences/${project._id}`, { preferences: updated });
      setTeamChatMuted(currentInApp);
      toast.success(currentInApp ? 'Team chat notifications muted' : 'Team chat notifications enabled');
    } catch {
      toast.error('Failed to update notification setting');
    }
  };

  if (!project) {
    if (urlProjectId) {
      return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading project...</div>;
    }
    return <div>No project data available</div>;
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleRenewalClick = () => {
    setRenewalDialogOpen(true);
  };

  const handleRenewalClose = () => {
    setRenewalDialogOpen(false);
    setNewEndDate(null);
    setError('');
  };

  const handleRenewalSubmit = async () => {
    if (!newEndDate) {
      setError('Please select a new end date');
      return;
    }

    try {
      const response = await axios.post(`/api/projects/${project._id}/renew`, {
        new_end_date: newEndDate,
      });
      handleRenewalClose();
      // Update project state and refetch data
      if (response.data) {
        setProject(response.data);
      }
      await fetchProjectData();
      toast.success('Project renewed successfully');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to renew project');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString();
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
      case 'Signed':
        return 'success';
      case 'Partially Paid':
        return 'warning';
      case 'Overdue':
      case 'Disputed':
        return 'error';
      case 'Billed':
      case 'Sent to Client':
        return 'info';
      case 'Final':
        return 'primary';
      case 'Draft':
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper function to safely extract chamber name
  const getChamberName = (chamber) => {
    if (!chamber) return 'Unknown Compartment';
    
    // If chamber is populated as an object, use the 'chamber' field
    if (typeof chamber === 'object' && chamber !== null) {
      return chamber.chamber || 'Unknown Compartment';
    }
    
    // If chamber is just an ID string, look it up in chamberDetails
    if (typeof chamber === 'string') {
      const chamberData = chamberDetails[chamber];
      if (chamberData) {
        return chamberData.chamber || 'Unknown Compartment';
      }
      
      // Fallback - show a shortened ObjectId
      return `Compartment ${chamber.substring(0, 8)}...`;
    }
    
    return 'Unknown Compartment';
  };

  // Helper function to safely extract unit display (showing both rate_name and unit)
  const getUnitDisplay = (unit) => {
    if (!unit) return 'Unknown Unit';

    // If unit is populated as an object, show base_unit (with short)
    if (typeof unit === 'object' && unit !== null) {
      const unitName = unit.base_unit || unit.unit || 'Unknown Unit';
      const unitShort = unit.unit_short || '';
      return unitShort && unitShort !== unitName ? `${unitName} (${unitShort})` : unitName;
    }
    
    // If unit is just an ID string, look it up in unitDetails
    if (typeof unit === 'string') {
      const unitData = unitDetails[unit];
      if (unitData) {
        const rateName = unitData.rate_name || 'Unknown Rate';
        const unitName = unitData.unit || 'Unknown Unit';
        return `${rateName} (${unitName})`;
      }
      
      // Fallback - show a shortened ObjectId
      return `Unit ${unit.substring(0, 8)}...`;
    }
    
    return 'Unknown Unit';
  };

  // Helper function to safely extract resource name
  const getResourceName = (resource) => {
    if (!resource) return 'Unknown Resource';
    
    // If resource is populated as an object, use the 'resource' field
    if (typeof resource === 'object' && resource !== null) {
      return resource.resource || 'Unknown Resource';
    }
    
    // If resource is just an ID string - show the ObjectId
    if (typeof resource === 'string') {
      return resource;
    }
    
    return 'Unknown Resource';
  };

  // Helper function to safely extract unit rate
  const getUnitRate = (unit, rateId) => {
    if (!unit) return 0;
    
    if (typeof unit === 'object' && unit !== null) {
      // New schema: rates is an array
      if (unit.rates && Array.isArray(unit.rates)) {
        if (rateId) {
          const rateObj = unit.rates.find(r => r.rate_id === rateId);
          if (rateObj) return rateObj.rate || 0;
        }
        return unit.rates[0]?.rate || 0;
      }
      // Legacy fallback
      return unit.rate || 0;
    }
    
    if (typeof unit === 'string') {
      const unitData = unitDetails[unit];
      if (unitData) {
        if (unitData.rates && Array.isArray(unitData.rates)) {
          return unitData.rates[0]?.rate || 0;
        }
        return unitData.rate || 0;
      }
    }
    
    return 0;
  };

  // Add helper functions to handle both schema formats
  const getChamberAssignments = (project) => {
    if (project.chamber_assignments && Array.isArray(project.chamber_assignments)) {
      return project.chamber_assignments;
    }
    return project.assigned_chambers || [];
  };

  const getResourceAssignments = (project) => {
    if (project.resource_assignments && Array.isArray(project.resource_assignments)) {
      return project.resource_assignments;
    }
    return project.assigned_resources || [];
  };

  const getInvoiceStatus = (invoice) => {
    // Legacy invoices keep their old status; the new lifecycle does not apply.
    if (!invoice.is_legacy && invoice.workflow_status) {
      return invoice.workflow_status;
    }
    if (!invoice.status_history || invoice.status_history.length === 0) {
      return 'Unknown';
    }
    
    const sortedHistory = [...invoice.status_history].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
    
    return sortedHistory[0].status;
  };

  // Refresh just the invoices list (used after distribution/signing updates).
  const refetchInvoices = async () => {
    try {
      const res = await axios.get(`/api/invoices/by-project/${project._id}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setInvoices(data);
      if (distributionInvoice) {
        const updated = data.find(inv => inv.invoice_id === distributionInvoice.invoice_id);
        if (updated) setDistributionInvoice(updated);
      }
      if (paymentsInvoice) {
        const updated = data.find(inv => inv.invoice_id === paymentsInvoice.invoice_id);
        if (updated) setPaymentsInvoice(updated);
      }
    } catch (err) {
      console.error('Error refreshing invoices:', err);
    }
  };

  const handleOpenDistribution = (invoice) => {
    setDistributionInvoice(invoice);
    setDistributionDialogOpen(true);
  };

  const handleCloseDistribution = () => {
    setDistributionDialogOpen(false);
    setDistributionInvoice(null);
  };

  const handleOpenPayments = (invoice) => {
    setPaymentsInvoice(invoice);
    setPaymentsDialogOpen(true);
  };

  const handleClosePayments = () => {
    setPaymentsDialogOpen(false);
    setPaymentsInvoice(null);
  };

  const handleOpenDispute = (invoice) => {
    setDisputeInvoice(invoice);
    setDisputeReason('');
    setDisputeDialogOpen(true);
  };

  const handleCloseDispute = () => {
    if (isSubmittingDispute) return;
    setDisputeDialogOpen(false);
    setDisputeInvoice(null);
    setDisputeReason('');
  };

  // Raising a dispute pauses signing on this invoice and notifies the admins.
  const handleSubmitDispute = async () => {
    if (!disputeInvoice) return;
    const reason = disputeReason.trim();
    if (!reason) {
      toast.error('Please describe what is wrong with this invoice.');
      return;
    }
    setIsSubmittingDispute(true);
    try {
      await axios.patch(
        `/api/invoices/${project.project_id || project._id}/${disputeInvoice.invoice_id}/dispute`,
        { reason }
      );
      toast.success('Dispute submitted. An admin will review this invoice.');
      setDisputeDialogOpen(false);
      setDisputeInvoice(null);
      setDisputeReason('');
      refetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit the dispute');
    } finally {
      setIsSubmittingDispute(false);
    }
  };

  const handleViewInvoicePDF = async (invoice) => {
    try {
      const response = await axios.get(
        `/api/invoices/${invoice._id}/pdf?invoice_id=${invoice.invoice_id}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF');
    }
  };

  const handleDownloadInvoicePDF = async (invoice) => {
    try {
      const response = await axios.get(
        `/api/invoices/${invoice._id}/pdf?invoice_id=${invoice.invoice_id}`,
        { responseType: 'blob' }
      );

      let filename = `invoice-${getInvoiceNumber(invoice.invoice_id)}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(?:(?:"([^"]*)")|([^;\n]*))/i);
        if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
          filename = filenameMatch[1] || filenameMatch[2];
        }
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/projects');
    }
  };

  const contentArea = (
    <div className="content-area">
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 2 }}
        >
          Back to Projects
        </Button>
          
          {/* The project's identity card. Themed rather than plain white: a
              faint wash of the deployment's colour with a solid bar on top, so
              the page opens on something that reads as this app. */}
          <Paper
            elevation={3}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: 'var(--brand-primary-softer, #fdf5f6)',
              borderTop: '4px solid var(--brand-primary, #c41e3a)',
              borderRadius: '6px'
            }}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, color: 'var(--brand-primary-text, #c41e3a)' }}
                >
                  {project.project_title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <Chip
                    label={project.project_status}
                    color={getStatusColor(project.project_status)}
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRenewalClick}
                    disabled={project.project_status !== 'In Progress'}
                    size="small"
                  >
                    Renew Project
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <CalendarToday sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                    Start: {formatDate(project.project_start_date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <CalendarToday sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                    End: {formatDate(project.project_end_date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <LocationOn sx={{ mr: 1, verticalAlign: 'middle', fontSize: 16 }} />
                    {project.facility?.facility_name}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Tabs 
            value={selectedTab} 
            onChange={handleTabChange} 
            sx={{
              mb: 3,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                minWidth: 'auto',
                px: 3,
                borderRadius: '6px 6px 0 0',
                transition: 'background-color 0.12s ease',
              },
              // Only the selected option is filled, with a light wash of the
              // theme colour — the bar itself stays plain.
              '& .MuiTab-root.Mui-selected': {
                backgroundColor: 'var(--brand-primary-soft, #f7e3e6)',
                color: 'var(--brand-primary-text, #c41e3a)',
                fontWeight: 600,
              }
            }}
          >
            <Tab label="Overview" value="overview" />
            <Tab label="Team" value="team" />
            <Tab label="Resources" value="resources" />
            {!isResourceOnly && <Tab label="Requests" value="requests" />}
            <Tab label="Invoices" value="invoices" />
            <Tab label="Documents" value="documents" />
            <Tab value="chat" label={
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {isResourceOnly ? 'Activity' : 'Activity & Chat'}
                {chatUnreadCount > 0 && (
                  <span style={{
                    background: 'var(--brand-primary, #c41e3a)',
                    color: '#fff',
                    borderRadius: '10px',
                    padding: '1px 7px',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    minWidth: '18px',
                    textAlign: 'center',
                    lineHeight: '16px',
                  }}>
                    {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                  </span>
                )}
              </span>
            } />
          </Tabs>

          {selectedTab === 'overview' && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Project Timeline */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <AccessTime sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Project Timeline
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {project.project_timeline || 'No timeline provided'}
                  </Typography>
                </Grid>

                {/* Experimental Design Summary */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <Science sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Experimental Design Summary
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {project.experimental_design_summary || 'No design summary provided'}
                  </Typography>
                </Grid>

                {/* Experiment Details */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <LocalFlorist sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Experiment Details
                  </Typography>
                  <List dense>
                    {project.experiment_details?.map((detail, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <LocalFlorist sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Species"
                          secondary={detail.species?.name || detail.species_not_listed || 'Not specified'}
                        />
                      </ListItem>
                    ))}
                    <ListItem>
                      <ListItemIcon>
                        <WaterDrop sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Water Type"
                        secondary={project.experiment_details?.[0]?.water?.name || 'Not specified'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Grass sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Substrate"
                        secondary={project.experiment_details?.[0]?.substrate?.name || 'Not specified'}
                      />
                    </ListItem>
                  </List>
                </Grid>

                {/* Environmental Conditions */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <Thermostat sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Environmental Conditions
                  </Typography>
                  <List dense>
                    {project.experiment_conditions?.map((condition, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemIcon>
                            <Thermostat sx={{ fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Temperature"
                            secondary={`Day: ${condition.temp_day}°C, Night: ${condition.temp_night}°C`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Water sx={{ fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Humidity"
                            secondary={`Day: ${condition.humidity_day}%, Night: ${condition.humidity_night}%`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Light sx={{ fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Photoperiod"
                            secondary={`On: ${formatClock(condition.photo_on)}, Off: ${formatClock(condition.photo_off)}`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            <Co2 sx={{ fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="CO2"
                            secondary={`Day: ${condition.co2_day}ppm, Night: ${condition.co2_night}ppm`}
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                </Grid>

                {/* Pots Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <LocalFlorist sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Pots & Equipment
                  </Typography>
                  <List dense>
                    {project.pots?.map((pot, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <LocalFlorist sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${pot.pot_size?.size || 'Unknown Size'} Pots`}
                          secondary={`Quantity: ${pot.quantity}`}
                        />
                      </ListItem>
                    ))}
                    {project.number_of_carts && (
                      <ListItem>
                        <ListItemIcon>
                          <LocalFlorist sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Carts"
                          secondary={`Quantity: ${project.number_of_carts}`}
                        />
                      </ListItem>
                    )}
                    {project.preferred_space && (
                      <ListItem>
                        <ListItemIcon>
                          <LocationOn sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Preferred Space"
                          secondary={project.preferred_space}
                        />
                      </ListItem>
                    )}
                  </List>
                </Grid>

                {/* Funding Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <AttachMoney sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Funding Information
                  </Typography>
                  <List dense>
                    {project.funding?.map((fund, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <AttachMoney sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Account Number"
                          secondary={displayAccountNumber(fund.account_number) || 'Not specified'}
                        />
                      </ListItem>
                    ))}
                    {project.funding?.map((fund, index) => (
                      fund.source_of_funds && (
                        <ListItem key={`source-${index}`}>
                          <ListItemIcon>
                            <AttachMoney sx={{ fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Source of Funds"
                            secondary={fund.source_of_funds}
                          />
                        </ListItem>
                      )
                    ))}
                  </List>
                </Grid>

                {/* Special Environmental Requirements */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <Science sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Special Environmental Requirements
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {project.special_environmental_requirements || 'No special requirements specified'}
                  </Typography>
                </Grid>

                {/* Additional Comments */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <Comment sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Additional Comments
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {project.additional_comments || 'No additional comments'}
                  </Typography>
                </Grid>

                {/* Research Safety Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                    <Science sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                    Research Safety Information
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <LocalFlorist sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Uses Transgenic Plants"
                        secondary={project.uses_transgenic_plants ? 'Yes' : 'No'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <Science sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Uses Pathogen"
                        secondary={project.uses_pathogen ? 'Yes' : 'No'}
                      />
                    </ListItem>
                    {project.uses_transgenic_plants || project.uses_pathogen ? (
                      <ListItem>
                        <ListItemIcon>
                          <Science sx={{ fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Safety Training Verified"
                          secondary={project.safety_training_verified ? 'Yes' : 'No'}
                        />
                      </ListItem>
                    ) : null}
                  </List>
                </Grid>
              </Grid>
            </Paper>
          )}

          {selectedTab === 'team' && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  <People sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                  Project Team
                </Typography>
                {project.users?.some(u => u.email === user?.email && ['PI', 'Co-PI'].includes(u.role?.role)) && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setAddMemberDialogOpen(true)}
                    startIcon={<People />}
                  >
                    Add Team Member
                  </Button>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Status</TableCell>
                      {project.users?.some(u => u.email === user?.email && ['PI', 'Co-PI'].includes(u.role?.role)) && (
                        <TableCell align="right">Actions</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {project.users?.map((member, index) => (
                      <TableRow key={index} style={{ backgroundColor: isUserDeleted(member) ? '#fff3cd' : 'transparent' }}>
                        <TableCell>{getUserDisplayName(member)}</TableCell>
                        <TableCell>
                          {member.role?.role || member.role}
                        </TableCell>
                        <TableCell>{member.department?.department_name || member.customDepartment || 'N/A'}</TableCell>
                        <TableCell>{getUserDisplayEmail(member)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={getUserStatusDisplay(member)} 
                            size="small"
                            color={isUserDeleted(member) ? 'error' : (member.status === 'Active' ? 'success' : 'default')}
                          />
                        </TableCell>
                        {project.users?.some(u => u.email === user?.email && ['PI', 'Co-PI'].includes(u.role?.role)) && (
                          <TableCell align="right">
                            {member.role?.role !== 'PI' && (
                              <IconButton aria-label="Delete"
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(member.email)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Add Member Dialog */}
              <Dialog 
                open={addMemberDialogOpen} 
                onClose={() => { setAddMemberDialogOpen(false); resetAddMemberForm(); }}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Add Team Member</DialogTitle>
                <DialogContent>
                  <Box sx={{ pt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                      Email Address *
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Enter email address"
                      value={newMemberEmail}
                      onChange={handleEmailChange}
                      disabled={isCheckingEmail}
                      helperText={
                        isCheckingEmail 
                          ? 'Checking email...' 
                          : foundUser 
                            ? '✓ Existing user found' 
                            : isNewUser 
                              ? 'New user - please fill in their details below'
                              : ''
                      }
                      sx={{ mb: 2 }}
                      size="small"
                      InputProps={{
                        endAdornment: isCheckingEmail ? <CircularProgress size={20} /> : null
                      }}
                    />

                    {foundUser && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                          ✓ {foundUser.first_name} {foundUser.last_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Department: {foundUser.department?.department_name || foundUser.customDepartment || 'Not specified'}
                        </Typography>
                      </Box>
                    )}

                    {isNewUser && (
                      <Box sx={{ mb: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                        <Typography variant="subtitle2" sx={{ color: '#e65100', mb: 2 }}>
                          New User - Please provide their details
                        </Typography>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                              First Name *
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="First name"
                              value={newMemberFirstName}
                              onChange={(e) => setNewMemberFirstName(e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                              Last Name *
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="Last name"
                              value={newMemberLastName}
                              onChange={(e) => setNewMemberLastName(e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                              Department
                            </Typography>
                            <Select
                              fullWidth
                              value={newMemberDepartment}
                              onChange={(e) => setNewMemberDepartment(e.target.value)}
                              displayEmpty
                              size="small"
                            >
                              <MenuItem value="">Select department</MenuItem>
                              <MenuItem value="not_listed">Department not listed</MenuItem>
                              {[...departments].sort((a, b) => 
                                (a.department_name || '').localeCompare(b.department_name || '')
                              ).map((dept) => (
                                <MenuItem key={dept._id} value={dept._id}>
                                  {dept.department_name} ({dept.department_code})
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                          {newMemberDepartment === 'not_listed' && (
                            <Grid item xs={12}>
                              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                                Custom Department Name
                              </Typography>
                              <TextField
                                fullWidth
                                value={newMemberCustomDepartment}
                                onChange={(e) => setNewMemberCustomDepartment(e.target.value)}
                                size="small"
                                placeholder="Enter department name"
                              />
                            </Grid>
                          )}
                        </Grid>
                        
                        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#666' }}>
                          This user will be added to the project. When they sign up, they will see this project in their account.
                        </Typography>
                      </Box>
                    )}

                    <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary' }}>
                      Role *
                    </Typography>
                    <Select
                      fullWidth
                      value={newMemberRole}
                      onChange={(e) => setNewMemberRole(e.target.value)}
                      size="small"
                    >
                      <MenuItem value="Staff">Staff</MenuItem>
                      <MenuItem value="Student">Student</MenuItem>
                      <MenuItem value="Co-PI">Co-PI</MenuItem>
                    </Select>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setAddMemberDialogOpen(false); resetAddMemberForm(); }}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddMember}
                    disabled={
                      (!foundUser && !isNewUser) || 
                      isSubmitting || 
                      (isNewUser && (!newMemberFirstName.trim() || !newMemberLastName.trim()))
                    }
                    variant="contained"
                    color="primary"
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {isSubmitting ? 'Adding...' : (isNewUser ? 'Add New Member' : 'Add Member')}
                  </Button>
                </DialogActions>
              </Dialog>
            </Paper>
          )}

          {selectedTab === 'resources' && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                <AttachMoney sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                Assigned Resources
              </Typography>
              
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Compartments
              </Typography>
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Compartment</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>End Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getChamberAssignments(project).map((chamber, index) => {
                      // Support both new schema (assignments) and old schema (units)
                      const assignments = chamber.assignments || chamber.units || [];
                      return assignments.map((assignment, aIndex) => {
                        const chamberName = getChamberName(chamber.chamber);
                        // For new schema, get rate info from chamber's unit
                        const unitDisplay = assignment.rate_id 
                          ? `${assignment.rate_id}` 
                          : getUnitDisplay(assignment.unit);
                        
                        return (
                          <TableRow key={`${index}-${aIndex}`}>
                            <TableCell>{chamberName}</TableCell>
                            <TableCell>{unitDisplay}</TableCell>
                            <TableCell>{assignment.quantity}</TableCell>
                            <TableCell>{formatDate(assignment.start_date)}</TableCell>
                            <TableCell>{formatDate(assignment.end_date)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={assignment.status} 
                                size="small"
                                color={assignment.status === 'Active' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 260, whiteSpace: 'pre-wrap' }}>
                              {assignment.comment
                                ? <Typography variant="body2" color="text.secondary">{assignment.comment}</Typography>
                                : <Typography variant="caption" color="text.secondary">—</Typography>}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })}
                    {getChamberAssignments(project).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No compartment assignments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
                Other Resources
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Resource</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Usage Dates</TableCell>
                      <TableCell>Rate</TableCell>
                      <TableCell>Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getResourceAssignments(project).map((resource, index) => {
                      const resourceName = getResourceName(resource.resource || resource);
                      
                      let unitDisplay = 'Unknown Unit';
                      let unitRate = 0;
                      let rateName = '';
                      let rateBasisLabel = '';
                      
                      const fmtBasis = (b) => b ? `/ ${b}` : '/ unit';
                      
                      if (resource.resource && typeof resource.resource === 'object' && resource.resource.unit) {
                        unitDisplay = getUnitDisplay(resource.resource.unit);
                        unitRate = getUnitRate(resource.resource.unit, resource.rate_id);
                        const rateObj = resource.resource.unit?.rates?.find(r => r.rate_id === resource.rate_id);
                        rateName = rateObj?.display_name || resource.rate_id || '';
                        rateBasisLabel = fmtBasis(rateObj?.rate_basis);
                      } else if (resource.unit) {
                        unitDisplay = getUnitDisplay(resource.unit);
                        unitRate = getUnitRate(resource.unit, resource.rate_id);
                        const rateObj = resource.unit?.rates?.find(r => r.rate_id === resource.rate_id);
                        rateName = rateObj?.display_name || resource.rate_id || '';
                        rateBasisLabel = fmtBasis(rateObj?.rate_basis);
                      }
                      
                      const totalCost = (resource.quantity || 0) * unitRate;
                      
                      return (
                        <TableRow key={resource._id || index}>
                          <TableCell>{resourceName}</TableCell>
                          <TableCell>{unitDisplay}</TableCell>
                          <TableCell>{resource.quantity || 0}</TableCell>
                          <TableCell>
                            {(resource.usage_periods || []).length > 0
                              ? resource.usage_periods.map((p, i) => (
                                <Typography key={p._id || i} variant="caption" component="div">
                                  {formatUsagePeriod(p)}{p.note ? ` · ${p.note}` : ''}
                                </Typography>
                              ))
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {rateName && <Typography variant="caption" color="text.secondary">{rateName}: </Typography>}
                            ${unitRate.toFixed(2)} {rateBasisLabel}
                          </TableCell>
                          <TableCell>${totalCost.toFixed(2)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {getResourceAssignments(project).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No resource assignments found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {selectedTab === 'requests' && (
            <ProjectRequestsTab
              project={project}
              requests={requests}
              refresh={fetchProjectData}
            />
          )}

          {selectedTab === 'invoices' && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
                <Receipt sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                Project Invoices
              </Typography>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Box sx={{ p: 2, color: 'error.main' }}>
                  {error}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice Number</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payments</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices && invoices.length > 0 ? (
                        invoices.map((invoice) => (
                          <React.Fragment key={invoice._id || invoice.invoice_id}>
                            <TableRow>
                              <TableCell>Invoice #{getInvoiceNumber(invoice.invoice_id)}</TableCell>
                              <TableCell>{formatDate(invoice.generation_date)}</TableCell>
                              <TableCell>${invoice.total_amount?.toFixed(2)}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                                  <Chip 
                                    label={getInvoiceStatus(invoice)} 
                                    size="small"
                                    color={getStatusColor(getInvoiceStatus(invoice))}
                                  />
                                  {invoice.is_legacy && (
                                    <Chip label="Legacy" size="small" variant="outlined" color="secondary" />
                                  )}
                                </Stack>
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const payments = invoice.payments || [];
                                  if (payments.length === 0) {
                                    return (
                                      <Typography variant="body2" color="text.secondary">
                                        No payment
                                      </Typography>
                                    );
                                  }
                                  const paid = payments.reduce(
                                    (sum, p) => sum + (Number(p?.amount) || 0),
                                    0
                                  );
                                  const balance = (invoice.total_amount || 0) - paid;
                                  return (
                                    <Box>
                                      <Typography variant="body2">
                                        ${paid.toFixed(2)} paid
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color={balance > 0 ? 'error.main' : 'text.secondary'}
                                      >
                                        {balance > 0
                                          ? `$${balance.toFixed(2)} balance`
                                          : 'Paid in full'}
                                      </Typography>
                                      <Box>
                                        <Button
                                          size="small"
                                          variant="text"
                                          onClick={() => handleOpenPayments(invoice)}
                                          sx={{ textTransform: 'none', fontSize: '0.72rem', p: 0, minWidth: 0 }}
                                        >
                                          {payments.length === 1
                                            ? 'View 1 payment'
                                            : `View ${payments.length} payments`}
                                        </Button>
                                      </Box>
                                    </Box>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
                                  {(() => {
                                    const status = getInvoiceStatus(invoice);
                                    if (status !== 'Billed' && status !== 'Signed') return null;
                                    // Only the PI (who sets the split) or a designated payer
                                    // (a Co-PI added to this invoice) may open the payment
                                    // dialog. Staff, students, and non-designated Co-PIs only
                                    // get view/download.
                                    const isPI = userProjectRole === 'PI';
                                    const isDesignatedPayer = (invoice.payer_distribution || []).some(
                                      (p) => String(p.user_id?._id || p.user_id) === String(userId)
                                    );
                                    if (!isPI && !isDesignatedPayer) return null;
                                    const label = status === 'Signed' ? 'View Distribution' : 'Manage Payment';
                                    return (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color={status === 'Signed' ? 'success' : 'primary'}
                                        onClick={() => handleOpenDistribution(invoice)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                      >
                                        {label}
                                      </Button>
                                    );
                                  })()}
                                  {/* Disputing is how the project pushes back on an invoice
                                      it has been asked to sign, so it is offered only while
                                      the invoice is awaiting signatures and only to the
                                      PI/Co-PI who answer for the project. */}
                                  {getInvoiceStatus(invoice) === 'Billed' && !invoice.is_legacy && isPIorCoPI && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => handleOpenDispute(invoice)}
                                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                    >
                                      Dispute
                                    </Button>
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleViewInvoicePDF(invoice)}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                  >
                                    View PDF
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleDownloadInvoicePDF(invoice)}
                                    sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                  >
                                    Download PDF
                                  </Button>
                                </Stack>
                              </TableCell>
                            </TableRow>
                            {invoice.dispute?.is_open && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ py: 1, borderBottom: 'none' }}>
                                  <Alert severity="warning">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Under dispute{invoice.dispute.raised_at ? ` since ${formatDate(invoice.dispute.raised_at)}` : ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {invoice.dispute.reason}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Signing is paused. An admin will review the invoice and send it back once it is sorted out.
                                    </Typography>
                                  </Alert>
                                </TableCell>
                              </TableRow>
                            )}
                            {/* A re-sent invoice is just Billed again, so nothing here
                                announces a resolution — only the admin's reply, when
                                there is one, so the team knows what changed. */}
                            {!invoice.dispute?.is_open && invoice.dispute?.resolution_note && (
                              <TableRow>
                                <TableCell colSpan={6} sx={{ py: 1, borderBottom: 'none' }}>
                                  <Alert severity="info">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      Response from the facility
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {invoice.dispute.resolution_note}
                                    </Typography>
                                  </Alert>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No invoices found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          )}

          {selectedTab === 'documents' && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <ProjectDocuments projectId={project._id} user={user} />
            </Paper>
          )}

          {selectedTab === 'chat' && (
            <Paper elevation={3} sx={{ p: 0, overflow: 'hidden' }}>
              {isResourceOnly ? (
                // Resource-only projects: activity log only, no team chat
                <ProjectChat
                  projectId={project._id}
                  user={user}
                  maxHeight="650px"
                  channel="activity"
                />
              ) : (
              <>
              {/* Sub-tab bar */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #e2e8f0',
                px: 2,
                py: 0.5,
                background: '#f8fafc',
              }}>
                <Box sx={{ display: 'flex', gap: '4px' }}>
                  {isPIorCoPI && (
                    <button
                      onClick={() => setChatSubTab('activity')}
                      style={{
                        padding: '7px 18px',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: chatSubTab === 'activity' ? 600 : 400,
                        background: chatSubTab === 'activity' ? '#0f172a' : 'transparent',
                        color: chatSubTab === 'activity' ? '#fff' : '#475569',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      Activity
                      {activityUnreadCount > 0 && (
                        <span style={{
                          background: chatSubTab === 'activity' ? '#ef4444' : 'var(--brand-primary, #c41e3a)',
                          color: '#fff',
                          borderRadius: '10px',
                          padding: '1px 6px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          minWidth: '16px',
                          textAlign: 'center',
                        }}>
                          {activityUnreadCount > 99 ? '99+' : activityUnreadCount}
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setChatSubTab('team_chat')}
                    style={{
                      padding: '7px 18px',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: chatSubTab === 'team_chat' ? 600 : 400,
                      background: chatSubTab === 'team_chat' ? '#0f172a' : 'transparent',
                      color: chatSubTab === 'team_chat' ? '#fff' : '#475569',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    Team Chat
                    {teamChatUnreadCount > 0 && (
                      <span style={{
                        background: chatSubTab === 'team_chat' ? '#ef4444' : 'var(--brand-primary, #c41e3a)',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '1px 6px',
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        minWidth: '16px',
                        textAlign: 'center',
                      }}>
                        {teamChatUnreadCount > 99 ? '99+' : teamChatUnreadCount}
                      </span>
                    )}
                  </button>
                </Box>

                {/* Mute/Unmute toggle for team chat */}
                {chatSubTab === 'team_chat' && (
                  <Tooltip title={teamChatMuted ? 'Unmute notifications' : 'Mute notifications'}>
                    <IconButton size="small" onClick={toggleTeamChatMute}>
                      {teamChatMuted
                        ? <NotificationsOffIcon fontSize="small" sx={{ color: '#999' }} />
                        : <NotificationsIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Chat content */}
              {chatSubTab === 'activity' && isPIorCoPI && (
                <ProjectChat
                  projectId={project._id}
                  user={user}
                  maxHeight="650px"
                  channel="activity"
                />
              )}
              {chatSubTab === 'team_chat' && (
                <ProjectChat
                  projectId={project._id}
                  user={user}
                  maxHeight="650px"
                  channel="team_chat"
                  projectUsers={project?.users || []}
                />
              )}
              </>
              )}
            </Paper>
          )}
        </Box>

        <Dialog open={renewalDialogOpen} onClose={handleRenewalClose}>
          <DialogTitle>Renew Project</DialogTitle>
          <DialogContent>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="New End Date"
                value={newEndDate}
                onChange={setNewEndDate}
                renderInput={(params) => <TextField {...params} fullWidth sx={{ mt: 2 }} />}
              />
            </LocalizationProvider>
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRenewalClose}>Cancel</Button>
            <Button onClick={handleRenewalSubmit} variant="contained">
              Submit Renewal
            </Button>
          </DialogActions>
        </Dialog>

        {distributionInvoice && (
          <InvoiceDistributionDialog
            open={distributionDialogOpen}
            onClose={handleCloseDistribution}
            invoice={distributionInvoice}
            project={project}
            user={user}
            onUpdated={refetchInvoices}
          />
        )}

        {paymentsInvoice && (
          <InvoicePaymentsDialog
            open={paymentsDialogOpen}
            onClose={handleClosePayments}
            invoice={paymentsInvoice}
            invoiceNumber={getInvoiceNumber(paymentsInvoice.invoice_id)}
          />
        )}

        {/* Dispute an invoice: pauses signing and sends the reason to the admins */}
        <Dialog open={disputeDialogOpen} onClose={handleCloseDispute} maxWidth="sm" fullWidth>
          <DialogTitle>
            Dispute Invoice
            {disputeInvoice && (
              <Typography variant="body2" color="text.secondary">
                Invoice #{getInvoiceNumber(disputeInvoice.invoice_id)} — $
                {disputeInvoice.total_amount?.toFixed(2)}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Signing will be paused on this invoice and the facility admins will be notified.
              They will review it, make any corrections, and send it back for signatures.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              required
              label="What is wrong with this invoice?"
              placeholder="e.g. The compartment charge for March covers two weeks we were not using the chamber."
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              helperText={`${disputeReason.length}/2000 — be specific so the admin can fix it quickly.`}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseDispute} disabled={isSubmittingDispute}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmitDispute}
              disabled={isSubmittingDispute || !disputeReason.trim()}
              startIcon={isSubmittingDispute ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isSubmittingDispute ? 'Submitting...' : 'Submit Dispute'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );

  // Return with or without sidebar based on embedded prop
  if (embedded) {
    return contentArea;
  }

  return (
    <div className="projects-container">
      <div className="sidebar">
        <h2>Projects</h2>
        <ul>
          <li>
            <button
              type="button"
              onClick={() => navigate('/projects')}
              className={location.pathname.includes('/projects/view') ? 'active' : ''}
              aria-current={location.pathname.includes('/projects/view') ? 'page' : undefined}
            >
              Ongoing Projects
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => navigate('/projects/past')}
              className={location.pathname === '/projects/past' ? 'active' : ''}
              aria-current={location.pathname === '/projects/past' ? 'page' : undefined}
            >
              Past Projects
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => navigate('/projects/extend')}
              className={location.pathname === '/projects/extend' ? 'active' : ''}
              aria-current={location.pathname === '/projects/extend' ? 'page' : undefined}
            >
              Extend/Revise
            </button>
          </li>
        </ul>
      </div>
      {contentArea}
    </div>
  );
};

export default ProjectView;
